import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CheckCircle2, XCircle, MinusCircle, PlayCircle, ChevronDown } from "lucide-react";
import VideoExplanationPlayer from "./VideoExplanationPlayer";

const FILTERS = [
  { key: "incorrect", label: "Incorrect" },
  { key: "skipped", label: "Skipped" },
  { key: "all", label: "All" },
];

const statusOf = (selected, correct) => {
  if (!selected) return "skipped";
  return selected === correct ? "correct" : "incorrect";
};

export const MockTestReview = ({ test, userAnswers = {}, sessionId }) => {
  const [filter, setFilter] = useState("incorrect");
  const [visible, setVisible] = useState(5);
  const [videoIndex, setVideoIndex] = useState(null);

  const items = (test.questions || []).map((q, i) => {
    const selected = userAnswers[i] || userAnswers[String(i)] || null;
    return { index: i, q, selected, status: statusOf(selected, (q.correct_answer || "").toLowerCase()) };
  });

  const counts = {
    incorrect: items.filter((it) => it.status === "incorrect").length,
    skipped: items.filter((it) => it.status === "skipped").length,
    all: items.length,
  };

  const filtered = filter === "all" ? items : items.filter((it) => it.status === filter);
  const shown = filtered.slice(0, visible);

  return (
    <Card className="border-0 shadow-lg mt-4" data-testid="mock-review-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Question-wise Review</CardTitle>
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setFilter(f.key);
                setVisible(5);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                filter === f.key
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`mock-review-filter-${f.key}`}
            >
              {f.label} ({counts[f.key]})
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {shown.length === 0 && (
          <p className="text-sm text-gray-500 py-4 text-center" data-testid="mock-review-empty">
            Is category me koi question nahi hai. 🎉
          </p>
        )}

        {shown.map(({ index, q, selected, status }) => {
          const correct = (q.correct_answer || "").toLowerCase();
          return (
            <div
              key={index}
              className="p-4 rounded-xl border border-gray-200 bg-white"
              data-testid={`mock-review-item-${index}`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge variant="outline" className="text-xs">Q{index + 1}</Badge>
                {status === "correct" && (
                  <Badge className="bg-green-100 text-green-800 text-xs">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Correct
                  </Badge>
                )}
                {status === "incorrect" && (
                  <Badge className="bg-red-100 text-red-800 text-xs">
                    <XCircle className="w-3 h-3 mr-1" /> Incorrect
                  </Badge>
                )}
                {status === "skipped" && (
                  <Badge className="bg-gray-100 text-gray-700 text-xs">
                    <MinusCircle className="w-3 h-3 mr-1" /> Skipped
                  </Badge>
                )}
                {q.topic && <span className="text-xs text-gray-500">{q.topic}</span>}
              </div>

              <p className="text-sm font-medium text-gray-900 mb-3 leading-relaxed">{q.question}</p>

              <div className="space-y-1.5 mb-3">
                {["a", "b", "c", "d"].map((opt) => {
                  const isCorrect = opt === correct;
                  const isPicked = opt === selected;
                  return (
                    <div
                      key={opt}
                      className={`flex items-start gap-2 p-2 rounded-lg text-sm border ${
                        isCorrect
                          ? "border-green-400 bg-green-50"
                          : isPicked
                          ? "border-red-400 bg-red-50"
                          : "border-gray-100 bg-gray-50"
                      }`}
                    >
                      <span className="font-bold uppercase text-gray-600 w-4">{opt}</span>
                      <span className="flex-1 text-gray-800">{q.options?.[opt]}</span>
                      {isCorrect && <span className="text-xs text-green-700 font-semibold">Correct</span>}
                      {isPicked && !isCorrect && <span className="text-xs text-red-700 font-semibold">Your pick</span>}
                    </div>
                  );
                })}
              </div>

              {q.explanation && (
                <p className="text-xs text-gray-600 leading-relaxed mb-3 whitespace-pre-wrap">
                  <span className="font-semibold text-gray-700">Explanation: </span>
                  {q.explanation}
                </p>
              )}

              <Button
                size="sm"
                variant="outline"
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                onClick={() => setVideoIndex(index)}
                data-testid={`mock-watch-video-btn-${index}`}
              >
                <PlayCircle className="w-4 h-4 mr-1.5" /> Watch Video Explanation
              </Button>
            </div>
          );
        })}

        {filtered.length > shown.length && (
          <Button
            variant="ghost"
            className="w-full text-sm"
            onClick={() => setVisible((v) => v + 10)}
            data-testid="mock-review-load-more"
          >
            <ChevronDown className="w-4 h-4 mr-1" /> Load more ({filtered.length - shown.length} left)
          </Button>
        )}
      </CardContent>

      {videoIndex !== null && (
        <VideoExplanationPlayer
          open={videoIndex !== null}
          onOpenChange={(o) => !o && setVideoIndex(null)}
          questionId={test.questions?.[videoIndex]?.id}
          sessionId={sessionId}
          sessionType="mock_test"
          questionIndex={videoIndex}
        />
      )}
    </Card>
  );
};

export default MockTestReview;
